import React from 'react';
import { Battery, Zap, Calendar, Search, FileText, ClipboardCheck, Hammer, Settings, Building2 } from 'lucide-react';
import { BESS_PARTNER_LABEL, PageFooterSpeicher, formatGermanNumber, getBessPowerLabel, getSpeicherPowerLabel, getStorageExtensionText, isBessOffer, parseGermanNumber } from './ContentPage2Speicher';
import { DraggableElement } from '../../DraggableElement';

const DATA = {
  kundenname: 'Mustermann Gewerbe GmbH',
  kundenadresse: 'Industriestraße 15, 45678 Musterstadt',
  angebotsnummer: 'SP-2025-0203',
  leistung_kw: '250',
  leistung_mw: '1',
  leistung_label: 'bis zu 1 MW',
  einheiten: '2 × 125',
  stellflaeche: '10',
  laufzeit: '20',
  einmalig: '20.000',
  jaehrlich: '1.500',
  gesamt_jaehrlich: '30.000',
  vl_prozent: '5',
};

const processSteps = [
  { icon: Search, num: '1', title: 'Standortanalyse', desc: 'Kostenlose Erstbewertung Ihrer Stellfläche' },
  { icon: FileText, num: '2', title: 'Angebot', desc: 'Indikatives Angebot binnen Tagen' },
  { icon: ClipboardCheck, num: '3', title: 'Vertrag', desc: 'Transparenter Stellflächenvertrag' },
  { icon: Settings, num: '4', title: 'Genehmigung', desc: 'Netzanschluss und Abstimmung durch uns' },
  { icon: Hammer, num: '5', title: 'Bau', desc: 'Aufstellung und Anschluss des Speichers' },
];

const bessProcessSteps = [
  { icon: Search, num: '1', title: 'Standortanalyse', desc: 'Kostenlose Erstbewertung Ihrer Stellfläche' },
  { icon: FileText, num: '2', title: 'Angebot', desc: 'Indikatives BESS-Angebot binnen Tagen' },
  { icon: ClipboardCheck, num: '3', title: 'Vertrag', desc: 'Transparenter Stellflächenvertrag' },
  { icon: Settings, num: '4', title: 'Netzplanung', desc: 'Netzanschlussplanung durch EN:POWER' },
  { icon: Building2, num: '5', title: 'Baurecht', desc: 'Baurechtliche Entwicklung durch Solarea' },
  { icon: Hammer, num: '6', title: 'Bau', desc: 'Errichtung und Anschluss des Großspeichers' },
];

export const ContentPage3Speicher = ({ data = {}, editMode = false, showMarkers = true, selectedElements = [], elementPositions = {}, onSelectElement, onPositionChange }) => {
  const offerData = { ...DATA, ...data };
  const isBess = isBessOffer(offerData);
  const variantProcessSteps = isBess ? bessProcessSteps : processSteps;
  const powerLabel = getSpeicherPowerLabel(offerData);
  const bessPowerLabel = getBessPowerLabel(offerData);
  const extensionText = getStorageExtensionText(offerData);
  const runtimeText = isBess ? '20 Jahre + 2 × 5 Jahre Verlängerung' : `${offerData.laufzeit} Jahre`;
  const offerTypeLabel = isBess ? 'Großspeicher – indikativ' : 'Schwarmspeicher – indikativ';
  const bessMw = parseGermanNumber(offerData.leistung_mw);
  const oneTimePerMw = offerData.einmalig_pro_mw || (isBess && bessMw > 0 ? formatGermanNumber(parseGermanNumber(offerData.einmalig) / bessMw, 0) : '');
  const annualPerMw = offerData.jaehrlich_pro_mw || (isBess && bessMw > 0 ? formatGermanNumber(parseGermanNumber(offerData.jaehrlich) / bessMw, 0) : '');
  const noticeText = isBess
    ? `Alle Angaben sind indikativ und vorbehaltlich einer Netzprüfung, finalen technischen Planung und erforderlichen Baugenehmigung vor Baubeginn. ${BESS_PARTNER_LABEL} setzen das Projekt gemeinsam um: EN:POWER übernimmt Akquise, Netzplanung und Verkauf an IAB-Investoren; Solarea verantwortet Baurecht und Verkauf an Großinvestoren sowie Fonds. Für Sie entstehen keine Kosten für Prüfung, Genehmigung, Installation, Betrieb, Wartung und Versicherung.`
    : 'Alle Angaben sind indikativ und vorbehaltlich einer Netzprüfung. Keine Baugenehmigung erforderlich. EN:POWER trägt alle Kosten für Installation, Betrieb, Wartung und Versicherung. Bei Netzkapazität bis 1 MW skaliert die Vergütung entsprechend.';
  const dragProps = {
    editMode,
    showMarkers,
    selectedElements,
    elementPositions,
    onSelectElement,
    onPositionChange,
    dragAxis: 'y',
  };

  return (
  <div className="a4-page" data-testid="page-3-sp" style={{ display: 'flex', flexDirection: 'column' }}>
    <DraggableElement id="SP-S3-Heading" {...dragProps}>
    <div style={{ padding: '34px 48px 0', background: 'white' }}>
      <span className="section-overline">{isBess ? `Gemeinschaftsprojekt ${BESS_PARTNER_LABEL}` : 'Ihr Speicherprojekt'}</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px' }}>IHR VERGÜTUNGSANGEBOT</h2>
      <div className="orange-rule" />
    </div>
    </DraggableElement>

    {/* Address bar */}
    <DraggableElement id="SP-S3-Address" {...dragProps}>
    <div style={{ padding: '0 48px 18px', background: 'white' }}>
      <div data-no-override style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: '#F8FAFC', borderRadius: '7px', border: '1px solid #E2E8F0' }}>
        <Battery size={12} color="#E07000" strokeWidth={2} />
        <p className="proposal-body" style={{ fontSize: '10px', margin: 0 }}>
          <strong style={{ color: '#122A5C' }}>Standort:</strong>{' '}
          {offerData.kundenadresse} – Geplante Leistung: <strong style={{ color: '#122A5C' }}>{powerLabel}</strong>
        </p>
      </div>
    </div>
    </DraggableElement>

    {/* KPI tiles */}
    <DraggableElement id="SP-S3-KPI" {...dragProps}>
    <div data-no-override style={{ padding: '0 48px 20px', background: 'white' }}>
      <p className="proposal-body" style={{ fontSize: '10px', marginBottom: '12px', maxWidth: '520px' }}>
        Die Voranalyse Ihres Standorts ergab, vorbehaltlich einer finalen Netzprüfung{isBess ? ' und Genehmigungsprüfung' : ''}, folgende Eckdaten:
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <DraggableElement id="SP-S3-KPI-1" {...dragProps}>
        <div className="kpi-tile">
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <Battery size={11} color="#E07000" strokeWidth={2} />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Leistung</span>
          </div>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: isBess ? '24px' : '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
            {isBess ? bessPowerLabel : offerData.leistung_kw} {!isBess && <span style={{ fontSize: '13px', fontWeight: 600 }}>kW</span>}
          </p>
          <p className="proposal-body" style={{ fontSize: '9px' }}>{isBess ? 'Abhängig von Netzkapazität und Genehmigung' : `${offerData.einheiten} kW Einheiten`}</p>
        </div>
        </DraggableElement>
        <DraggableElement id="SP-S3-KPI-2" {...dragProps}>
        <div className="kpi-tile" style={{ borderLeftColor: '#E07000' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <Zap size={11} color="#E07000" strokeWidth={2} />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Stellfläche</span>
          </div>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
            {offerData.stellflaeche} <span style={{ fontSize: '13px', fontWeight: 600 }}>m²</span>
          </p>
          <p className="proposal-body" style={{ fontSize: '9px' }}>{isBess ? 'Indikative Fläche' : 'Reine Stellfläche'}</p>
        </div>
        </DraggableElement>
        <DraggableElement id="SP-S3-KPI-3" {...dragProps}>
        <div className="kpi-tile" style={{ borderLeftColor: '#64748B' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <Calendar size={11} color="#64748B" strokeWidth={2} />
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Laufzeit</span>
          </div>
          <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '28px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.03em', lineHeight: 1, margin: '0 0 3px' }}>
            {offerData.laufzeit} <span style={{ fontSize: '13px', fontWeight: 600 }}>Jahre</span>
          </p>
          <p className="proposal-body" style={{ fontSize: '9px' }}>{isBess ? '+ 2 × 5 Jahre Verlängerung' : '+ 2 × 5 J. Verlängerungsoption'}</p>
        </div>
        </DraggableElement>
      </div>
    </div>
    </DraggableElement>

    {/* Process flow */}
    <DraggableElement id="SP-S3-Process" {...dragProps}>
    <div style={{ background: '#F8FAFC', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0', padding: '18px 48px' }}>
      <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '10px' }}>Ablauf des Projekts</p>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${variantProcessSteps.length}, 1fr)`, position: 'relative' }}>
        <div style={{ position: 'absolute', top: '12px', left: isBess ? '7%' : '10%', right: isBess ? '7%' : '10%', height: '2px', background: 'linear-gradient(to right, #E07000, rgba(224,112,0,0.2))', zIndex: 0 }} />
        {variantProcessSteps.map((step, i) => {
          const Icon = step.icon;
          return (
            <DraggableElement key={i} id={`SP-S3-Process-${i + 1}`} {...dragProps} initialStyle={{ textAlign: 'center', position: 'relative', zIndex: 1, padding: '0 4px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: i < 2 ? '#122A5C' : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px', border: `2px solid ${i < 2 ? '#122A5C' : '#CBD5E1'}` }}>
                <Icon size={12} color={i < 2 ? 'white' : '#94A3B8'} strokeWidth={2} />
              </div>
              <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: isBess ? '8.8px' : '9.5px', fontWeight: 700, color: '#122A5C', marginBottom: '2px' }}>{step.title}</p>
              <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8px', color: '#64748B', lineHeight: 1.3 }}>{step.desc}</p>
            </DraggableElement>
          );
        })}
      </div>
    </div>
    </DraggableElement>

    <div className="section-divider" />

    {/* Pricing heading */}
    <DraggableElement id="SP-S3-Pricing" {...dragProps} initialStyle={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
    <div style={{ padding: '16px 48px 0', background: 'white' }}>
      <span className="section-overline">Persönliches Angebot</span>
      <h2 className="proposal-h2" style={{ fontSize: '19px' }}>IHRE VERGÜTUNGSOPTIONEN</h2>
      <div className="orange-rule" />
    </div>

    {/* Meta header */}
    <div style={{ padding: '0 48px 12px', background: 'white' }}>
      <div data-no-override style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 16px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
        {[
          { l: 'Auftraggeber', v: offerData.kundenname, c: '#122A5C' },
          { l: 'Angebots-Nr.', v: offerData.angebotsnummer, c: '#122A5C' },
          { l: 'Angebotstyp', v: offerTypeLabel, c: '#E07000' },
        ].map((item, i) => (
          <div key={i}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '7.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>{item.l}</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, color: item.c }}>{item.v}</p>
          </div>
        ))}
      </div>
    </div>

    {/* Two pricing options */}
    <div style={{ padding: '0 48px', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
        {/* Option 1: Einmalzahlung */}
        <DraggableElement id="SP-S3-Pricing-1" {...dragProps}>
        <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(224,112,0,0.25)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: '#E07000', padding: '10px 16px' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Option 1 — Einmalzahlung</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.75)', marginTop: '1px' }}>Einmalige Auszahlung bei Vertragsabschluss</p>
          </div>
          <div style={{ padding: '12px 16px', background: 'rgba(224,112,0,0.05)', borderBottom: '1px solid rgba(224,112,0,0.15)' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '3px' }}>Einmalige Auszahlung</p>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '26px', fontWeight: 800, color: '#E07000', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {offerData.einmalig},- <span style={{ fontSize: '14px', fontWeight: 600 }}>€</span>
            </p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8', marginTop: '2px' }}>netto zzgl. MwSt.</p>
          </div>
          <table className="pricing-table" style={{ flex: 1 }}>
            <tbody>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px', width: '50%' }}>Leistung</td><td style={{ fontSize: '9.5px' }}>{isBess ? powerLabel : `${offerData.leistung_kw} kW (${offerData.einheiten} kW)`}</td></tr>
              {isBess && oneTimePerMw && <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Zahlung pro MW</td><td style={{ fontSize: '9.5px' }}>{oneTimePerMw},- € netto / MW</td></tr>}
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Laufzeit</td><td style={{ fontSize: '9.5px' }}>{runtimeText}</td></tr>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>{isBess ? 'Verlängerungszahlung' : 'Verlängerung'}</td><td style={{ fontSize: '9.5px' }}>{extensionText}</td></tr>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Kosten für Sie</td><td style={{ fontSize: '9.5px', color: '#16A34A', fontWeight: 600 }}>Null €</td></tr>
            </tbody>
          </table>
        </div>
        </DraggableElement>

        {/* Option 2: Jährlich */}
        <DraggableElement id="SP-S3-Pricing-2" {...dragProps}>
        <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', border: '1.5px solid rgba(18,42,92,0.15)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: '#122A5C', padding: '10px 16px' }}>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '10px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Option 2 — Jährlich</p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: 'rgba(255,255,255,0.55)', marginTop: '1px' }}>Jährliche Zahlung über die gesamte Laufzeit</p>
          </div>
          <div style={{ padding: '12px 16px', background: 'rgba(18,42,92,0.04)', borderBottom: '1px solid rgba(18,42,92,0.1)' }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '3px' }}>Jährliche Auszahlung</p>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontSize: '26px', fontWeight: 800, color: '#122A5C', letterSpacing: '-0.02em', lineHeight: 1 }}>
              {offerData.jaehrlich},- <span style={{ fontSize: '14px', fontWeight: 600 }}>€</span>
            </p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#94A3B8', marginTop: '2px' }}>netto zzgl. MwSt. / Jahr</p>
          </div>
          <table className="pricing-table" style={{ flex: 1 }}>
            <tbody>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px', width: '50%' }}>Gesamte Vergütung</td><td style={{ fontSize: '9.5px', fontWeight: 700, color: '#122A5C' }}>{offerData.gesamt_jaehrlich},-€ netto ({isBess ? '20' : offerData.laufzeit} J.)</td></tr>
              {isBess && annualPerMw && <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Zahlung pro MW</td><td style={{ fontSize: '9.5px' }}>{annualPerMw},- € netto / MW / Jahr</td></tr>}
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Laufzeit</td><td style={{ fontSize: '9.5px' }}>{runtimeText}</td></tr>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>{isBess ? 'Verlängerungszahlung' : 'Verlängerung'}</td><td style={{ fontSize: '9.5px' }}>{extensionText}</td></tr>
              <tr><td style={{ fontWeight: 600, color: '#122A5C', fontSize: '9.5px' }}>Kosten für Sie</td><td style={{ fontSize: '9.5px', color: '#16A34A', fontWeight: 600 }}>Null €</td></tr>
            </tbody>
          </table>
        </div>
        </DraggableElement>
      </div>

      {/* Note */}
      <DraggableElement id="SP-S3-Notice" {...dragProps}>
      <div style={{ marginTop: '14px', padding: '9px 14px', background: '#FFFBF5', border: '1px solid #FED7AA', borderRadius: '7px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <span style={{ fontSize: '11px', lineHeight: 1, marginTop: '1px', flexShrink: 0 }}>&#9432;</span>
        <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '8.5px', color: '#92400E', lineHeight: 1.5, margin: 0 }}>
          {noticeText}
        </p>
      </div>
      </DraggableElement>
    </div>
    </DraggableElement>

    <PageFooterSpeicher page={3} variant={isBess ? 'bess' : offerData.offerVariant} />
  </div>
  );
};
